/**
 * Helper class for target countries. All methods and properties are static.
 *
 * File: \src\components\countries\CountryHelper.ts
 * Created Date: Friday, August 28th 2020, 15:07:56
 * Author: Vanya Lazarova
 * -----
 * Last Modified: July 16th 2024, 17:43:51
 * Modified By: Shahbaz Haider
 * -----
 * Copyright (c) 2017-2020 Liebherr-Hausgeräte GmbH
 */

import { useI18nStore } from '@/I18nStore';

export interface SupportedCountry {
  name: string;
  code: string;
}

/**
 * Target countries helper class
 *
 * @export
 * @class CountryHelper
 */
export class CountryHelper {
  /**
   * Definition of all available countries.
   *
   * @static
   * @type {string}
   * @memberof CountryHelper
   */
  public static readonly DE: string = 'DE';
  public static readonly AT: string = 'AT';
  public static readonly AU: string = 'AU';
  public static readonly GB: string = 'GB';
  public static readonly CH: string = 'CH';
  public static readonly PL: string = 'PL';
  public static readonly ES: string = 'ES';
  public static readonly DK: string = 'DK';
  public static readonly IT: string = 'IT';
  public static readonly FR: string = 'FR';
  public static readonly NL: string = 'NL';
  public static readonly BE: string = 'BE';
  public static readonly LU: string = 'LU';
  public static readonly IE: string = 'IE';
  public static readonly CZ: string = 'CZ';
  public static readonly HU: string = 'HU';
  public static readonly SI: string = 'SI';
  public static readonly SK: string = 'SK';
  public static readonly BG: string = 'BG';
  public static readonly GR: string = 'GR';
  public static readonly RO: string = 'RO';
  public static readonly US: string = 'US';

  public static readonly DEFAULT: string = 'DE';

  public static readonly SUPPORTED_COUNTRIES: string[] = [
    CountryHelper.DE,
    CountryHelper.AT,
    CountryHelper.AU,
    CountryHelper.GB,
    CountryHelper.CH,
    CountryHelper.PL,
    CountryHelper.ES,
    CountryHelper.DK,
    CountryHelper.FR,
    CountryHelper.IT,
    CountryHelper.NL,
    CountryHelper.BE,
    CountryHelper.IE,
    CountryHelper.LU,
    CountryHelper.CZ,
    CountryHelper.HU,
    CountryHelper.SI,
    CountryHelper.SK,
    CountryHelper.BG,
    CountryHelper.GR,
    CountryHelper.RO,
    CountryHelper.US
  ];

  /**
   * Makes sure to return a supported country name.
   *
   * @static
   *
   * @param {string} countryCode The code.
   * @returns {string} The name of that region/country.
   *
   * @memberof CountryHelper
   */
  static getCountryName(countryCode: string): string {
    const i18n = useI18nStore();
    switch (countryCode) {
      case this.DE:
        return i18n.t('general.countries.DE');

      case this.AT:
        return i18n.t('general.countries.AT');

      case this.GB:
        return i18n.t('general.countries.GB');

      case this.CH:
        return i18n.t('general.countries.CH');

      case this.PL:
        return i18n.t('general.countries.PL');

      case this.ES:
        return i18n.t('general.countries.ES');

      case this.DK:
        return i18n.t('general.countries.DK');

      case this.FR:
        return i18n.t('general.countries.FR');

      case this.IT:
        return i18n.t('general.countries.IT');

      case this.NL:
        return i18n.t('general.countries.NL');

      case this.BE:
        return i18n.t('general.countries.BE');

      case this.IE:
        return i18n.t('general.countries.IE');

      case this.LU:
        return i18n.t('general.countries.LU');

      case this.CZ:
        return i18n.t('general.countries.CZ');

      case this.HU:
        return i18n.t('general.countries.HU');

      case this.SI:
        return i18n.t('general.countries.SI');

      case this.SK:
        return i18n.t('general.countries.SK');

      case this.BG:
        return i18n.t('general.countries.BG');

      case this.GR:
        return i18n.t('general.countries.GR');

      case this.RO:
        return i18n.t('general.countries.RO');

      case this.AU:
        return i18n.t('general.countries.AU');

      case this.US:
        return i18n.t('general.countries.US');

      default:
        return 'Others';
    }
  }

  /**
   * Makes sure to return a supported country code. "DE" is the fallback.
   *
   * @static
   *
   * @param {string} country The country code.
   * @returns {string} The supported country code.
   *
   * @memberof CountryHelper
   */
  static getCountryCode(country: string): string {
    if (this.SUPPORTED_COUNTRIES.indexOf(country) >= 0) {
      return country;
    }

    return this.DE;
  }

  /**
   * Quick check to see if this is a supported country.
   *
   * @static
   *
   * @param {string} countryCode The code to check, like "DE" or "EN".
   * @returns {boolean} true if it's supported.
   *
   * @memberof CountryHelper
   */
  static isSupportedCountry(countryCode: string): boolean {
    return this.SUPPORTED_COUNTRIES.includes(countryCode);
  }

  /**
   * The possible countrycodes that the user can chose in the dialog.
   *
   * @private
   * @type {SupportedCountry[]}
   * @memberof CountryHelper
   */
  static get countryCodes(): SupportedCountry[] {
    return [
      { name: CountryHelper.getCountryName(CountryHelper.DE), code: CountryHelper.DE },
      { name: CountryHelper.getCountryName(CountryHelper.AT), code: CountryHelper.AT },
      { name: CountryHelper.getCountryName(CountryHelper.GB), code: CountryHelper.GB },
      { name: CountryHelper.getCountryName(CountryHelper.CH), code: CountryHelper.CH },
      { name: CountryHelper.getCountryName(CountryHelper.PL), code: CountryHelper.PL },
      { name: CountryHelper.getCountryName(CountryHelper.ES), code: CountryHelper.ES },
      { name: CountryHelper.getCountryName(CountryHelper.DK), code: CountryHelper.DK },
      { name: CountryHelper.getCountryName(CountryHelper.FR), code: CountryHelper.FR },
      { name: CountryHelper.getCountryName(CountryHelper.IT), code: CountryHelper.IT },
      { name: CountryHelper.getCountryName(CountryHelper.NL), code: CountryHelper.NL },
      { name: CountryHelper.getCountryName(CountryHelper.BE), code: CountryHelper.BE },
      { name: CountryHelper.getCountryName(CountryHelper.LU), code: CountryHelper.LU },
      { name: CountryHelper.getCountryName(CountryHelper.IE), code: CountryHelper.IE },
      { name: CountryHelper.getCountryName(CountryHelper.CZ), code: CountryHelper.CZ }
    ];
  }
}
